import { Customer } from "gen/customer/models_pb"
import { BoolFilter } from "gen/search/service/service_pb"
import { ReactNode, useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import { Chip } from "shared-web/components/base/informational/Chip"
import {
  AccessoryContext,
  usePopoverSelect,
  UsePopoverSelectProps,
  UsePopoverSelectRes,
} from "shared-web/components/composite/popover"

import { useSearchCustomersV2 } from "features/api/search"
import { urls } from "urls"

interface UseCustomersPopoverSelectProps
  extends Pick<
    UsePopoverSelectProps<Customer>,
    "selected" | "onSelect" | "defaultSelected"
  > {
  filter?: (customer: Customer) => boolean
}

export type UseCustomersPopoverSelectRes = UsePopoverSelectRes<Customer>

function useCustomersPopoverSelect(
  props: UseCustomersPopoverSelectProps
): UseCustomersPopoverSelectRes {
  const { filter } = props

  const { results, setQuery } = useSearchCustomersV2(
    { isActiveFilter: BoolFilter.TRUE },
    { ignoreSearchQuery: true }
  )

  const customers = useMemo(() => {
    const res: Customer[] = []
    for (const r of results) {
      if (!r.customer) continue
      if (filter && !filter(r.customer)) continue
      res.push(r.customer)
    }
    return res
  }, [results, filter])

  return usePopoverSelect({
    items: customers,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    onChangeQuery: setQuery,
    ...props,
  })
}

function keyExtractor(customer: Customer): string {
  return UUID.idString(customer.id)
}

function labelExtractor(customer: Customer): string {
  return customer.name
}

function accessoryExtractor(
  customer: Customer,
  context: AccessoryContext
): ReactNode | null {
  if (!customer.identifier) {
    return null
  }
  if (context === "button") {
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <Chip
          color="sky"
          text={customer.identifier}
          href={urls.customer(customer.id)}
        />
      </span>
    )
  }
  return <Chip color="sky" text={customer.identifier} />
}

export default useCustomersPopoverSelect
